/**
 * The score as a picture (1080×1350, the portrait size Instagram, WhatsApp and stories all crop
 * well) for players who share screenshots rather than text. Drawn on a canvas in the browser:
 * no server work, and the image carries the same spoiler-free information as the text paste
 * (tier colours, total, time, streak) plus the challenge link.
 */
import {
  GAME_DOMAIN,
  GAME_NAME,
  MAX_TOTAL,
  TIER_COLOR,
  TIER_LABEL,
  tierFor,
  tierForTotal,
  formatTime,
  type RoundSummary,
} from '@geosport/shared';
import type { ShareOutcome } from './share';

export const CARD_W = 1080;
export const CARD_H = 1350;

const INK = '#0b1020';
const INK2 = '#1c2540';
const ACCENT = '#f5b301';
const FONT = 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

/** The line under the score: what kind of round it was. */
export function cardSubtitle(s: Pick<RoundSummary, 'mode' | 'puzzleNo'>, special?: string): string {
  if (special) return special;
  if (s.mode === 'random') return 'Random round · practice';
  if (s.mode === 'archive') return `Puzzle #${s.puzzleNo} · replay`;
  return `Puzzle #${s.puzzleNo}`;
}

/** Where the picture sends people: a daily score links to its "beat my score" page. */
export function cardLink(s: Pick<RoundSummary, 'mode' | 'puzzleNo' | 'total'>): string {
  return s.mode === 'daily' ? `${GAME_DOMAIN}/challenge/${s.puzzleNo}/${s.total}` : GAME_DOMAIN;
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

export async function drawScoreCard(summary: RoundSummary, special?: string): Promise<Blob | null> {
  const canvas = document.createElement('canvas');
  canvas.width = CARD_W;
  canvas.height = CARD_H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const bg = ctx.createRadialGradient(CARD_W, 0, 80, CARD_W, 0, CARD_H * 1.1);
  bg.addColorStop(0, INK2);
  bg.addColorStop(1, INK);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  // Brand row.
  const logo = await loadImage('/icons/icon-192.png');
  if (logo) ctx.drawImage(logo, 80, 80, 88, 88);
  ctx.fillStyle = '#fff';
  ctx.font = `800 64px ${FONT}`;
  ctx.textBaseline = 'middle';
  ctx.fillText(GAME_NAME, logo ? 192 : 80, 126);

  ctx.fillStyle = 'rgba(255,255,255,0.65)';
  ctx.font = `600 40px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.fillText(cardSubtitle(summary, special), CARD_W / 2, 300);

  // The total, coloured by tier.
  const total = summary.total.toLocaleString('en-GB');
  ctx.fillStyle = TIER_COLOR[tierForTotal(summary.total)];
  ctx.font = `900 260px ${FONT}`;
  ctx.fillText(total, CARD_W / 2, 470);
  ctx.fillStyle = 'rgba(255,255,255,0.45)';
  ctx.font = `700 56px ${FONT}`;
  ctx.fillText(`out of ${MAX_TOTAL.toLocaleString('en-GB')}`, CARD_W / 2, 640);

  // One tile per question: colour = tier, no answers.
  const n = summary.bases.length;
  const size = 150;
  const gap = 32;
  const x0 = (CARD_W - (n * size + (n - 1) * gap)) / 2;
  summary.bases.forEach((base, i) => {
    const tier = tierFor(base);
    const x = x0 + i * (size + gap);
    const y = 740;
    ctx.fillStyle = TIER_COLOR[tier];
    ctx.beginPath();
    ctx.roundRect(x, y, size, size, 32);
    ctx.fill();
    ctx.fillStyle = INK;
    ctx.font = `800 44px ${FONT}`;
    ctx.fillText(`Q${i + 1}`, x + size / 2, y + size / 2 - 14);
    ctx.font = `700 26px ${FONT}`;
    ctx.fillText(TIER_LABEL[tier], x + size / 2, y + size / 2 + 30);
  });

  // Time and streak.
  const meta = [`⏱ ${formatTime(summary.timeMs)}`];
  if (summary.mode === 'daily' && summary.currentStreak >= 2) {
    meta.push(`🔥 ${summary.currentStreak}-day streak`);
  }
  ctx.fillStyle = 'rgba(255,255,255,0.8)';
  ctx.font = `600 44px ${FONT}`;
  ctx.fillText(meta.join('   ·   '), CARD_W / 2, 990);

  // Call to action.
  ctx.fillStyle = ACCENT;
  ctx.beginPath();
  ctx.roundRect(120, 1090, CARD_W - 240, 150, 40);
  ctx.fill();
  ctx.fillStyle = INK;
  ctx.font = `800 46px ${FONT}`;
  ctx.fillText(
    summary.mode === 'daily' ? 'Can you beat it?' : 'Play free every day',
    CARD_W / 2,
    1140,
  );
  ctx.font = `700 34px ${FONT}`;
  ctx.fillText(cardLink(summary), CARD_W / 2, 1192);

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
}

/** Share sheet with the picture where the browser can share files, otherwise a download. */
export async function shareScoreCard(
  summary: RoundSummary,
  special?: string,
): Promise<ShareOutcome | 'downloaded'> {
  const blob = await drawScoreCard(summary, special);
  if (!blob) return 'failed';
  const name = `${GAME_NAME.toLowerCase()}-${summary.mode === 'daily' ? summary.puzzleNo : 'practice'}-${summary.total}.png`;
  const file = new File([blob], name, { type: 'image/png' });
  if (typeof navigator.share === 'function' && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], text: summary.shareText });
      return 'shared';
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return 'failed';
    }
  }
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
    return 'downloaded';
  } catch {
    return 'failed';
  }
}
